import React, { useEffect, useState } from 'react';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Alert,
  Button,
  Form,
  Spinner,
  StatefulButton,
} from '@openedx/paragon';
import { Error } from '@openedx/paragon/icons';
import { Helmet } from 'react-helmet';
import { Navigate, useLocation } from 'react-router-dom';

import messages from './messages';
import { getAdditionalDetails, saveAdditionalDetails } from './service';
import { AuthCard, AuthLayout, FormGroup } from '../common-components';
import {
  COMPLETE_STATE,
  DEFAULT_REDIRECT_URL,
  DEFAULT_STATE,
  LOGIN_PAGE,
  PENDING_STATE,
} from '../data/constants';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

/**
 * Post-authentication capture of the admin-defined additional learner details.
 *
 * The fields are configured per organisation from the authoring MFE, so this
 * screen renders whatever the API returns and skips itself entirely when the org
 * configures nothing or the learner has already answered everything required.
 */
const AdditionalDetails = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();

  const authenticatedUser = getAuthenticatedUser() || location.state?.authenticatedUser;
  const redirectUrl = location.state?.registrationResult?.redirectUrl
    || `${getConfig().LMS_BASE_URL}${DEFAULT_REDIRECT_URL}`;

  const [checking, setChecking] = useState(true);
  const [schema, setSchema] = useState([]);
  const [values, setValues] = useState({});
  const [submitState, setSubmitState] = useState(DEFAULT_STATE);
  const [showError, setShowError] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // On mount, skip the screen entirely when there is nothing left to ask for.
  useEffect(() => {
    let isMounted = true;
    if (!authenticatedUser) {
      return undefined;
    }
    (async () => {
      try {
        const { schema: fields, values: saved } = await getAdditionalDetails();
        const nothingLeftToAsk = fields.length === 0
          || fields.every((field) => !field.required || !isBlank(saved[field.name]));
        if (nothingLeftToAsk) {
          window.location.href = redirectUrl;
          return;
        }
        if (isMounted) {
          setSchema(fields);
          setValues(fields.reduce((acc, field) => ({
            ...acc,
            [field.name]: saved[field.name] ?? (field.type === 'checkbox' ? false : ''),
          }), {}));
        }
      } catch (e) {
        // A failed lookup means we cannot know what to ask — continue rather
        // than trapping the learner on an empty form.
        window.location.href = redirectUrl;
        return;
      }
      if (isMounted) {
        setChecking(false);
      }
    })();
    return () => { isMounted = false; };
  }, [authenticatedUser, redirectUrl]);

  const handleChange = (e) => {
    const {
      name, type, checked, value,
    } = e.target;
    setValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const continueToRedirect = () => {
    window.location.href = redirectUrl;
  };

  const missingRequired = schema.filter(
    (field) => field.required && (field.type === 'checkbox' ? !values[field.name] : isBlank(values[field.name])),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (missingRequired.length > 0) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setShowError(false);
    setSubmitState(PENDING_STATE);
    try {
      // Send only answered fields: the API treats submissions as partial, and
      // an untouched optional field should stay unset rather than be stored blank.
      const payload = schema.reduce((acc, field) => {
        const value = values[field.name];
        if (field.type === 'checkbox' || !isBlank(value)) {
          acc[field.name] = field.type === 'checkbox' ? !!value : String(value).trim();
        }
        return acc;
      }, {});
      await saveAdditionalDetails(payload);
      setSubmitState(COMPLETE_STATE);
      continueToRedirect();
    } catch (err) {
      setShowError(true);
      setSubmitState(DEFAULT_STATE);
    }
  };

  if (!authenticatedUser) {
    return <Navigate to={LOGIN_PAGE} replace />;
  }

  // While checking we do not yet know whether there is anything to ask, and the
  // screen may redirect away without ever rendering the form — so show the
  // spinner alone rather than a heading for a form that might never appear.
  if (checking) {
    return (
      <AuthLayout>
        <Helmet>
          <title>{formatMessage(messages['additionalDetails.page.title'], { siteName: getConfig().SITE_NAME })}</title>
        </Helmet>
        <div className="d-flex justify-content-center w-100 py-4">
          <Spinner animation="border" variant="primary" id="additional-details-spinner" />
        </div>
      </AuthLayout>
    );
  }

  const requiredMessage = formatMessage(messages['additionalDetails.error.required']);

  /**
   * Render one admin-defined field.
   *
   * Uses the shared FormGroup with `stackedLabel` so the admin's label renders
   * visibly above the input. Paragon's
   * `floatingLabel` renders nothing under this theme, which is why the label
   * must not be passed that way.
   */
  const renderField = (field) => {
    const errorMessage = showValidation && missingRequired.some((f) => f.name === field.name)
      ? requiredMessage
      : '';

    if (field.type === 'checkbox') {
      return (
        <Form.Group key={field.name} isInvalid={!!errorMessage}>
          <Form.Checkbox
            id={field.name}
            name={field.name}
            checked={!!values[field.name]}
            onChange={handleChange}
          >
            {field.label}
          </Form.Checkbox>
          {errorMessage && (
            <Form.Control.Feedback type="invalid" className="form-text-size" hasIcon={false}>
              {errorMessage}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      );
    }

    if (field.type === 'select') {
      return (
        <FormGroup
          key={field.name}
          as="select"
          name={field.name}
          value={values[field.name]}
          stackedLabel
          floatingLabel={field.label}
          handleChange={handleChange}
          errorMessage={errorMessage}
          options={() => [
            <option value="" key="placeholder" disabled>
              {formatMessage(messages['additionalDetails.select.placeholder'])}
            </option>,
            ...(field.options || []).map(([optionValue, optionLabel]) => (
              <option value={optionValue} key={optionValue}>{optionLabel}</option>
            )),
          ]}
        />
      );
    }

    // 'phone' maps to tel; 'text', 'date' and 'number' are native input types.
    // A textarea has no meaningful `type`, so it falls back to the default.
    const inputTypes = { phone: 'tel', textarea: 'text' };

    return (
      <FormGroup
        key={field.name}
        as={field.type === 'textarea' ? 'textarea' : 'input'}
        type={inputTypes[field.type] || field.type}
        name={field.name}
        value={values[field.name]}
        stackedLabel
        floatingLabel={field.label}
        handleChange={handleChange}
        errorMessage={errorMessage}
      />
    );
  };

  return (
    <AuthLayout>
      <Helmet>
        <title>{formatMessage(messages['additionalDetails.page.title'], { siteName: getConfig().SITE_NAME })}</title>
      </Helmet>
      <AuthCard
        title={formatMessage(messages['additionalDetails.page.heading'])}
        subtitle={formatMessage(messages['additionalDetails.page.subheading'])}
        footer={(
          <Button
            variant="link"
            className="auth-card__skip p-0"
            onClick={continueToRedirect}
          >
            {formatMessage(messages['additionalDetails.skip.button'])}
          </Button>
        )}
      >
        <Form id="additional-details-form" name="additional-details-form" onSubmit={handleSubmit}>
          {showError && (
            <Alert id="additional-details-submit-error" variant="danger" icon={Error}>
              {formatMessage(messages['additionalDetails.error.submit'])}
            </Alert>
          )}
          {schema.map((field) => renderField(field))}
          <StatefulButton
            name="additional-details-submit"
            id="additional-details-submit"
            type="submit"
            variant="primary"
            className="auth-card__submit"
            state={submitState}
            labels={{
              default: formatMessage(messages['additionalDetails.submit.button']),
              pending: '',
            }}
            onClick={handleSubmit}
            onMouseDown={(event) => event.preventDefault()}
          />
        </Form>
      </AuthCard>
    </AuthLayout>
  );
};

export default AdditionalDetails;
